import { getIn } from 'formik';
import * as React from 'react';
import styled from 'styled-components';
import { Box } from '../';
// import { StyledSystemProps } from '../../../theme/StyledSystemProps';
import { Label, Span } from '../../../typography';

// type CustomProps = StyledSystemProps & {
//   color?: string;
//   defaultCountry?: string;
//   label?: string;
//   name: string;
//   placeholder?: string;
//   value?: any;
//   disabled?: boolean;
//   changeHandler?: (option: { value: any }) => void;
// };

const standardizeZANumber = (val: string): string => {
  if (!val) return '+27';

  // Remove all non-digit characters
  let digits = val.replace(/\D/g, '');

  // Handle different input scenarios
  if (digits.startsWith('27')) {
    // Already has country code
    digits = digits.substring(2); // Remove the '27' to process the rest
  } else if (digits.startsWith('0')) {
    // Has leading zero (local format)
    digits = digits.substring(1); // Remove the leading '0'
  }

  // Reject if the first digit after country code is 0
  if (digits.startsWith('0')) {
    // Return just +27 if user tries to add 0 after country code
    return '+27';
  }

  // Limit to 9 digits after country code
  digits = digits.substring(0, 9);

  // Always return +27 followed by the digits (max 12 chars total)
  return '+27' + digits;
};

const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  width: 100%;
`;

const FlagIcon = styled.span`
  position: absolute;
  left: 10px;
  font-size: 20px;
  pointer-events: none;
  user-select: none;
`;

const StyledInput = styled.input<{ $hasError?: boolean }>`
  background-color: #fff;
  border-radius: 4px;
  border: 1px solid ${props => (props.$hasError ? '#e53935' : 'rgba(50, 50, 93, 0.2)')};
  min-height: 38px;
  padding: 0 10px 0 40px;
  outline: none;
  transition: border-color 0.2s;
  width: 100%;
  font-size: 14px;

  &:focus {
    border-color: ${props => (props.$hasError ? '#e53935' : '#2684ff')};
  }
`;

const StyledFormPhone: React.FC<any> = props => {
  const { field, form, meta, label, minWidth, width, flex, mb, ml, mr, mt, ...rest } = props;

  const displayValue = field.value ? standardizeZANumber(field.value) : '+27';

  // Count only digits (excluding spaces and other formatting)
  const digitCount = field.value ? field.value.replace(/\D/g, '').length : 0;
  const lengthError = field.value && digitCount !== 11 ? 'Number must be 12 digits' : null;
  const formikError = getIn(form.errors, field.name);
  const displayError = formikError || lengthError;
  const isTouched = getIn(form.touched, field.name);
  const showError = !!(isTouched && displayError);

  React.useEffect(() => {
    if (field.value && field.value !== displayValue) {
      form.setFieldValue(field.name, displayValue);
    }
  }, [field.value, displayValue, field.name, form]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (!value || value === '+27') {
      form.setFieldValue(field.name, '');
      return;
    }

    // Strict enforcement: standardize and prevent invalid formats
    const standardized = standardizeZANumber(value);
    form.setFieldValue(field.name, standardized);
  };

  const containerProps = { minWidth, width, flex, mb, ml, mr, mt };

  return (
    <Box {...containerProps}>
      {label && <Label htmlFor={field.name}>{label}</Label>}

      <InputWrapper>
        <FlagIcon>🇿🇦</FlagIcon>
        <StyledInput
          {...rest}
          type="tel"
          id={field.name}
          name={field.name}
          value={displayValue}
          onChange={handleChange}
          onBlur={() => form.setFieldTouched(field.name, true)}
          $hasError={showError}
          maxLength={12}
          placeholder="+27620055667"
        />
      </InputWrapper>

      {showError && (
        <Span color="red" fontSize="12px" mt={1} display="block">
          {displayError}
        </Span>
      )}
    </Box>
  );
};

export default StyledFormPhone;

StyledFormPhone.defaultProps = {
  mb: 3,
  flex: 1,
  defaultCountry: 'ZA',
  minWidth: '150px',
  width: '100%',
  color: '#fff',
};
